import { isPlainObject } from 'lodash'
import * as AT from './_types'
import fetchApi from '../../services/fetchApi'
import { initSettingTypeKeys } from '../constants'

export const updateReviewsOffset = reviewsOffset => dispatch =>
  dispatch({
    type: AT.MESSAGE_SETTINGS_SET_REVIEWS_OFFSET,
    reviewsOffset,
  })

export const updateSocialLinks = links => dispatch =>
  dispatch({
    type: AT.MESSAGE_SETTINGS_SET_SOCIAL_LINKS,
    links,
  })

export const receiveMessageSettings = settingsData => dispatch => {

  const initialSetting = {
    byId: {},
    customMessageSettings: [],
    idsByType: initSettingTypeKeys(),
    newApptSettings: {},
    missedCallSettings: {},
    reviewSettings: {},
    schedulingSettings: {},
  }

  const {
    byId,
    customMessageSettings,
    idsByType,
    newApptSettings,
    missedCallSettings,
    offset,
    reviewSettings,
    schedulingSettings,
  } = shapeSettings(settingsData, initialSetting)

  dispatch(updateReviewsOffset(offset))
  dispatch({
    type: AT.MESSAGE_SETTINGS_RECEIVE,
    byId,
    idsByType,
    customMessageSettings,
    reviewSettings,
    schedulingSettings,
    newApptSettings,
    missedCallSettings,
  })
}

export const fetchSocialLinks = locationId => dispatch => {
  const url = locationId
    ? `/account/locations/links?location_id=${locationId}`
    : '/account/locations/links'
  fetchApi(url, 'GET').then(response => {
    dispatch(updateSocialLinks(response))
  })
}

export const updateSocialLink = (type, link, locationId) => dispatch => {
  const body = JSON.stringify({
    type,
    link,
    location_id: locationId,
  })
  return fetchApi('/account/locations/links', 'PATCH', { body }).then(() => {
    dispatch(fetchSocialLinks(locationId))
  })
}

export const fetchMessageSettings =
  (locationId, getTemplate = false) =>
  dispatch => {
    const url = locationId
      ? `/message_settings?location_id=${locationId}`
      : '/message_settings/'
    const fetchUrl = getTemplate
      ? `${locationId ? `${url}&` : `${url.slice(0, -1)}?`}template=true`
      : url
    return fetchApi(fetchUrl, 'GET').then(response => {
      dispatch(receiveMessageSettings(response))
    })
  }

export const updateReviewTypesOrOffset = (setting, locationId) => dispatch => {
  const fetchBody = JSON.stringify(setting)
  return fetchApi('/review/setting', 'PATCH', {
    body: fetchBody,
    location_id: locationId,
  })
    .then(res => {
      dispatch(receiveMessageSettings(res))
      return Promise.resolve(res)
    })
    .catch(error => Promise.resolve(error))
}

export const toggleActiveReviewMessage =
  (locationId, messageType, toggle) => dispatch => {
    const body = JSON.stringify({
      active: toggle,
      type: messageType,
      location_id: locationId,
    })
    return fetchApi('/review/active', 'POST', { body })
      .then(res => {
        dispatch(receiveMessageSettings(res))
        return Promise.resolve(res)
      })
      .catch(error => Promise.resolve(error))
  }

export const createCustomMessageSetting = setting => dispatch => {
  const fetchBody = JSON.stringify(setting)
  return fetchApi('/custom_message_settings', 'POST', {
    body: fetchBody,
  }).then(res => {
    dispatch(receiveMessageSettings(res))
  })
}

export const updateCustomMessageSetting = setting => dispatch => {
  const fetchBody = JSON.stringify(setting)
  return fetchApi('/custom_message_settings', 'PATCH', {
    body: fetchBody,
  }).then(res => {
    dispatch(receiveMessageSettings(res))
  })
}

export const destroyCustomMessageSetting = setting => dispatch => {
  const fetchBody = JSON.stringify(setting)
  return fetchApi('/custom_message_settings', 'DELETE', {
    body: fetchBody,
  }).then(res => {
    dispatch(receiveMessageSettings(res))
  })
}

export const updateMessageSetting =
  (settingId, changes) => (dispatch, getState) => {
    const currSetting = getState().messageSettings.byId[settingId] || {}

    const {
      offset,
      body,
      subject,
      always_send,
      appointment_types,
      setting_name,
      template_name,
    } = changes

    const template = { body, subject }
    const fetchUrl = `/message_settings/${settingId}`

    const fetchBody = JSON.stringify({
      always_send,
      appointment_types,
      message_type: currSetting.message_type,
      offset,
      setting_name,
      template,
      template_name,
    })

    return fetchApi(fetchUrl, 'PATCH', { body: fetchBody }).then(setting => {
      dispatch({
        type: AT.MESSAGE_SETTINGS_UPDATE,
        settingId: setting.id,
        nextSetting: processSetting(setting),
      })

      return setting
    })
  }

export const createMessageSetting = (initialSetting, changes) => dispatch => {
  const setting = prepareSettingForCreation(initialSetting, changes)

  const fetchBody = JSON.stringify(setting)
  return fetchApi('/message_settings', 'POST', { body: fetchBody }).then(
    savedSetting => {
      const { id, message_setting_type } = savedSetting

      dispatch({
        type: AT.MESSAGE_SETTINGS_CREATE,
        settingId: id,
        nextSetting: processSetting(savedSetting),
        messageSettingType: message_setting_type,
      })
      return savedSetting
    },
  )
}

export const activateSetting = (settingId, opts = {}) =>
  changeActiveForSetting(settingId, true, opts)

export const deactivateSetting = (settingId, opts = {}) =>
  changeActiveForSetting(settingId, false, opts)

export const undoDeactivate = settingId => dispatch =>
  dispatch(activateSetting(settingId)).then(() => {
    dispatch({
      type: AT.MESSAGE_SETTINGS_UNDO_DEACTIVATE,
      settingId,
    })
  })

const settingsReducer = (settingsArr, initialSetting) =>
  settingsArr.reduce((result, setting) => {
    const { id, message_setting_type: type } = setting

    result.byId[id] = processSetting(setting)
    if (!result.idsByType.hasOwnProperty(type)) {
      result.idsByType[type] = []
    }
    result.idsByType[type].push(id)
    return result
  }, initialSetting)

function shapeSettings(
  {
    settings,
    scheduling_settings: schedulingSettings,
    new_appt_settings: newApptSettings,
    custom_message_settings: customSettings,
    review_settings: reviews,
    missed_call_settings: missedCallSettings,
  },
  initialSetting,
) {
  // OK to mutate this object in transit. Is created in this function only, and
  // is not part of state.
  settingsReducer(settings, initialSetting)

  if (customSettings) {
    const filteredCustomSettings = customSettings.filter(setting => setting.appointment_types.length > 0)
    initialSetting.customMessageSettings = filteredCustomSettings
  }

  if (reviews && !!Object.keys(reviews).length) {
    const {
      follow_up_email: followUpEmail,
      follow_up_text: followUpText,
      initial_request_email: initialRequestEmail,
      initial_request_text: initialRequestText,
    } = reviews
    initialSetting.reviewSettings.followUpEmail = processSetting(followUpEmail)
    initialSetting.reviewSettings.followUpText = processSetting(followUpText)
    initialSetting.reviewSettings.initialRequestEmail =
      processSetting(initialRequestEmail)
    initialSetting.reviewSettings.initialRequestText =
      processSetting(initialRequestText)
    initialSetting.offset = initialRequestText.offset
  }

  if (schedulingSettings && !!Object.keys(schedulingSettings).length) {
    const {
      openchair_email: openchairEmail,
      openchair_text: openchairText,
      patient_portal_email: patientPortalEmail,
    } = schedulingSettings
    initialSetting.schedulingSettings.openchairEmail =
      processSetting(openchairEmail)
    initialSetting.schedulingSettings.openchairText =
      processSetting(openchairText)
    initialSetting.schedulingSettings.patientPortalEmail =
      processSetting(patientPortalEmail)
  }

  if (newApptSettings && !!Object.keys(newApptSettings).length) {
    const { new_appt_email: newApptEmail, new_appt_text: newApptText } =
      newApptSettings
    initialSetting.newApptSettings.newApptEmail =
      processSetting(newApptEmail)
    initialSetting.newApptSettings.newApptText =
      processSetting(newApptText)
  }

  if (missedCallSettings && !!Object.keys(missedCallSettings).length) {
    const { missed_call_email: missedCallEmail, missed_call_text: missedCallText } = missedCallSettings
    initialSetting.missedCallSettings.missedCallEmail =
      processSetting(missedCallEmail)
    initialSetting.missedCallSettings.missedCallText =
      processSetting(missedCallText)
  }
  return initialSetting
}

function processSetting(setting) {
  if (!setting) return null
  const { template } = setting
  delete setting.template
  // watch out for template coming back with Ruby < 2.0 style hash keys
  if (typeof template === 'string') {
    const { body, subject } = JSON.parse(template)
    return { ...setting, body, subject }
  } else if (isPlainObject(template)) {
    const { body, subject } = template
    return { ...setting, body, subject }
  }
  return setting
}

function changeActiveForSetting(settingId, nextActive, opts) {
  const { dispatchOnly, newApptSettingBypass, missedCallSettingBypass } = opts

  return (dispatch, getState) => {
    let messageSettingType;
    if (newApptSettingBypass) {
      messageSettingType = 'new_appt';
    } else if (missedCallSettingBypass) {
      messageSettingType = 'missed_call';
    } else {
      messageSettingType = getState().messageSettings.byId[settingId].message_setting_type;
    }

    const type = nextActive
      ? AT.MESSAGE_SETTINGS_ACTIVATE
      : AT.MESSAGE_SETTINGS_DEACTIVATE

    const body = JSON.stringify({ active: nextActive })

    dispatch({
      type,
      messageSettingType,
      settingId,
    })

    if (!dispatchOnly) {
      return fetchApi(`/message_settings/${settingId}`, 'PATCH', { body })
    }
    return Promise.resolve()
  }
}

// this method clones object then deletes a few properties before sending to API
function prepareSettingForCreation(initialSetting, additions) {
  const { message_setting_type, message_type, active } = initialSetting
  const {
    always_send,
    body,
    subject,
    offset,
    custom_setting_id,
    location_id,
    template_name,
  } = additions

  const template =
    initialSetting.message_type === 'email' ? { body, subject } : { body }

  // shallow clone. be sure to use body, subject, offset from `additions`
  const prepared = {
    ...initialSetting,
    setting: {
      always_send,
      offset,
      template,
      active,
      message_setting_type,
      message_type,
      template_name,
    },
    custom_setting_id,
    location_id,
  }

  delete prepared.tmpId
  delete prepared.body
  delete prepared.subject
  delete prepared.always_send
  delete prepared.offset
  delete prepared.template
  delete prepared.active
  delete prepared.message_setting_type
  delete prepared.message_type
  delete prepared.template_name

  return prepared
}



// WEBPACK FOOTER //
// ./src/state/actions/message-settings.js